import { useContext, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  useParams,
  NavLink,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import { Tabs, Col, Row, Tag, Button, Modal, Tooltip } from "antd";
import _ from "lodash";
import moment from "moment/moment";
import ProgressBar from "react-bootstrap/ProgressBar";
import {
  ArrowLeft,
  Plus,
  MessageCircle,
  ArrowLeftRight,
  Building2,
  Tag as TagIcon,
  ChevronUp,
} from "lucide-react";
import AttractiveEmptyState from "../common/AttractiveEmptyState";
import Info from "../components/leads-details/Info";
import Notes from "../components/leads-details/Notes";
import Tasks from "../components/leads-details/Tasks";
import Stepsbar from "../components/leads-details/Stepsbar";

import {
  getLeadDetails,
  getLeadTasksStatusList,
  getEmailHistoryByLeadId,
  markAsRead,
} from "../actionCreator/leadDetailsActionCreator";
import {
  getLeadStatusList,
  getLeadRatingList,
  transferLeads,
} from "../actionCreator/leadListingActionCreater";
import EmailHistory from "../components/leads-details/EmailHistory";
import LeadActivityDrawer from "../components/lead-search/LeadActivityDrawer";
import EmailForm from "../common/EmailForm";
import { ReactComponent as Designation } from "../assets/svg/icons/designation.svg";
import { ReactComponent as MailOpen } from "../assets/svg/icons/mail-open.svg";
import { ReactComponent as Call } from "../assets/svg/icons/call.svg";
import { ReactComponent as WebLink } from "../assets/svg/icons/web-link.svg";
import { ReactComponent as Location } from "../assets/svg/icons/location-icon.svg";
import { ReactComponent as Zoom } from "../assets/svg/icons/zoom-in-area.svg";
import { ReactComponent as LeadInfo } from "../assets/svg/icons/lead-detail-form.svg";
import { ReactComponent as Activity } from "../assets/svg/icons/activities.svg";
import { ReactComponent as Note } from "../assets/svg/icons/notes.svg";
import { ReactComponent as Email } from "../assets/svg/icons/email.svg";

import hot from "../assets/svg/hot.svg";
import cold from "../assets/svg/cold.svg";
import warm from "../assets/svg/warm.svg";
import { AuthContext } from "../context/AuthContext";
import { subscriptionPlans, userRoles } from "../config";

import profile from "../assets/svg/icons/lead-profile.svg";
import leadInfo from "../assets/svg/icons/lead-info.svg";
import TaskForm from "../components/leads-details/TaskForm";
import {
  getAllUsers,
  getCurrentUserPermissions,
} from "../actionCreator/userManagementActionCreator";
import { getUserInfo } from "../utils/utils";
import TransferLeadsDrawer from "../components/lead-search/TransferLeadsDrawer";
import { useAlert } from "../context/AlertProvider";

const LeadDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const [activeTab, setActiveTab] = useState("1");
  const [task, setTask] = useState();
  const [isAddTask, setAddTask] = useState(false);
  const [editTask, setEditTask] = useState();
  const [historyFieldFilter, setHistoryFieldFilter] = useState();
  const leadDetail = useSelector(
    (state) => state.LeadDetailsReducer.leadDetails,
  );
  const leadNotes = useSelector((state) => state.LeadDetailsReducer.leadNotes);
  const leadRemarks = useSelector(
    (state) => state.LeadDetailsReducer.leadRemarks,
  );
  const unreadNotifications = useSelector(
    (state) => state.LeadDetailsReducer.unreadNotifications,
  );
  const { isLeadView, isEmailSetupView, subscription } =
    useContext(AuthContext);
  const { showAlert } = useAlert();
  const [isTransfer, setIsTransfer] = useState(false);
  const currentUser = getUserInfo();
  const usersData = useSelector((state) => state.UserManagementReducer.users);
  const users = Array.isArray(usersData) ? usersData : usersData?.content || [];

  const getLeadOwnerName = () => {
    if (!leadDetail) return "";
    if (leadDetail.ownerName) return leadDetail.ownerName;
    const empId =
      leadDetail.employeeId ||
      (typeof leadDetail.employee === "object"
        ? leadDetail.employee?.id
        : leadDetail.employee);
    if (empId) {
      const match = users.find((u) => u.id === empId);
      if (match) {
        return (
          `${match.firstName || ""} ${match.lastName || ""}`.trim() ||
          match.login
        );
      }
    }
    return "";
  };
  const leadOwnerName = getLeadOwnerName();
  const canTransferLead =
    leadDetail?.allowedActions?.includes("TRANSFER") ||
    currentUser?.role === userRoles?.admin;

  useEffect(() => {
    if (!isLeadView) {
      navigate("/dashboard-view");
    }
  }, [isLeadView, navigate]);

  useEffect(() => {
    if (type === "email") {
      setActiveTab("4");
    }
  }, [type]);

  useEffect(() => {
    const markRead = async (leadId) => {
      await dispatch(markAsRead(leadId));
    };
    if (activeTab === "4") {
      const leadId = parseInt(id);
      const notiClientIds = unreadNotifications?.map((d) => d?.clientLeadId);
      const isUnread = notiClientIds.includes(leadId);
      if (leadId && isUnread) {
        markRead(leadId);
      }
    }
  }, [unreadNotifications, dispatch, id, activeTab]);


  const items = [
    {
      key: "1",
      isVisible: true,
      label: (
        <span className="px-30 tw-flex tw-items-center tw-gap-2">
          <LeadInfo />
          Lead Details
        </span>
      ),
      children: (
        <Info
          setActiveTab={(tabIndex) => changeTabIndex(tabIndex)}
          onOpenHistory={setHistoryFieldFilter}
          onOpenTransfer={() => setIsTransfer(true)}
        />
      ),
    },
    {
      key: "2",
      isVisible: ![subscriptionPlans?.trail].includes(
        subscription?.subscriptionPackage?.name,
      ),
      label: (
        <span className="px-30 tw-flex tw-items-center tw-gap-2">
          <Activity />
          Add Notes
        </span>
      ),
      children: <Notes setActiveTab={(tabIndex) => changeTabIndex(tabIndex)} />,
    },
    {
      key: "3",
      isVisible: ![subscriptionPlans?.trail].includes(
        subscription?.subscriptionPackage?.name,
      ),
      label: (
        <span className="px-30 tw-flex tw-items-center tw-gap-2">
          <Note />
          Activities
        </span>
      ),
      children: (
        <Tasks
          setTask={setTask}
          setEditTask={(val) => {
            setAddTask(true);
            setEditTask(val);
          }}
          setAddTask={() => setAddTask(true)}
          setActiveTab={(tabIndex) => changeTabIndex(tabIndex)}
        />
      ),
    },
    {
      key: "4",
      isVisible: ![subscriptionPlans?.trail].includes(
        subscription?.subscriptionPackage?.name,
      ),
      label: (
        <span className="px-30 tw-flex tw-items-center tw-gap-2">
          <Email /> Email
        </span>
      ),
      children: <EmailHistory />,
    },
  ];

  const changeTabIndex = (tabIndex) => {
    setActiveTab(tabIndex);
  };

  useEffect(() => {
    if (id) {
      dispatch(getLeadDetails(id));
      dispatch(getLeadStatusList());
      dispatch(getLeadRatingList());
      dispatch(getLeadTasksStatusList());
      dispatch(getAllUsers({ sort: "firstName,asc", size: 1000 }));
      if (currentUser?.id) {
        dispatch(getCurrentUserPermissions(currentUser.id));
      }
    }
  }, [id, dispatch, currentUser?.id]);

  useEffect(() => {
    const finalData = [...leadNotes, ...leadRemarks];
    let activityWholeData = [];
    let completedActivityData = [];
    let isMeetingActivity = false;
    if (finalData.length) {
      finalData.forEach((item, index) => {
        let isLeadNote = item?.note ? 1 : 0;
        let title = "";
        if (isLeadNote) {
          title = item.note;
          item.status = "open";
        } else {
          title = item.title;
        }
        if (title === "meeting" || title === "Meeting") {
          isMeetingActivity = true;
        } else {
          activityWholeData.push({
            title: title,
            status: item?.status,
            data: item,
          });
        }
        if (item.status?.trim() === "completed") {
          completedActivityData.push({
            title: title,
            status: item?.status,
            data: item,
          });
        }
      });
    }
    const groupedActivityData = _.groupBy(activityWholeData, "title");
    const groupedActivityCompletedData = _.groupBy(
      completedActivityData,
      "title",
    );
    let totalActivity = Object.keys(groupedActivityData).length;
    let totalClosedActivity = Object.keys(groupedActivityCompletedData).length;
    let totalActicvityPercentage = 0;
    let totalClosedActicvityPercentage = 0;
    if (totalActivity > 0) {
      if (totalActivity >= 5) {
        totalActicvityPercentage = 10;
      } else {
        totalActicvityPercentage = totalActivity * 2;
      }
    }
    if (totalClosedActivity > 0) {
      let perActivityPerc = 5 / totalActivity;
      totalClosedActicvityPercentage = perActivityPerc * totalClosedActivity;
    }
    let statusActivityPercentage = 0;
    if (isMeetingActivity) {
      statusActivityPercentage = 10;
    }
    statusActivityPercentage +=
      totalActicvityPercentage + totalClosedActicvityPercentage;
    let listToRender = [];
    if (Object.keys(leadDetail).length && leadDetail?.status) {
      let stepsCnt = 0;
      if (leadDetail?.status === 11) {
        stepsCnt = 1;
      } else if (leadDetail?.status === 12) {
        stepsCnt = 2;
      } else if (leadDetail?.status === 13) {
        stepsCnt = 3;
      } else if (leadDetail?.status === 14) {
        stepsCnt = 4;
      }
      if (leadDetail?.status === 15) {
        stepsCnt = 5;
      } else {
      }
      let clArray = [
        "primary",
        "secondary",
        "danger",
        "warning",
        "info",
        "success",
      ];
      if (leadDetail?.status === 16) {
        listToRender.push(
          <ProgressBar label="100%" variant={clArray[0]} now={100} key={1} />,
        );
      } else if (leadDetail?.status === 17 || leadDetail?.status === 18) {
        listToRender.push(
          <ProgressBar label="0%" variant={clArray[1]} now={0} key={1} />,
        );
      } else {
        let totalPercentage = Math.round(
          stepsCnt * 15 + statusActivityPercentage,
        );
        if (totalPercentage <= 20) {
          listToRender.push(
            <ProgressBar variant={clArray[2]} now={totalPercentage} key={1} />,
          );
        } else if (totalPercentage <= 40) {
          listToRender.push(
            <ProgressBar variant={clArray[3]} now={totalPercentage} key={1} />,
          );
        } else if (totalPercentage <= 60) {
          listToRender.push(
            <ProgressBar variant={clArray[5]} now={totalPercentage} key={1} />,
          );
        } else {
          listToRender.push(
            <ProgressBar variant={clArray[6]} now={totalPercentage} key={1} />,
          );
        }
      }
    }
  }, [leadDetail, leadNotes, leadRemarks]);

  return (
    <>
      <div
        id="leads"
        className="wrapper p-20 tw-font-inter tw-text-text-primary"
      >
        <div id="content-wrapper" className="tw-flex tw-flex-col">
          <div>
            <Row gutter={[20, 20]}>
              <Col lg={18}>
                <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 tw-rounded-2xl tw-p-5 tw-mb-6 tw-shadow-sm tw-flex tw-flex-col lg:tw-flex-row lg:tw-items-center lg:tw-justify-between tw-gap-5">
                  <div className="tw-flex tw-items-center tw-gap-4 tw-flex-1 tw-min-w-0">
                    <div
                      className="tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-size-10 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 hover:tw-border-theme/35 hover:tw-bg-indigo-50/20 tw-text-slate-500 hover:tw-text-theme tw-transition-all tw-shrink-0"
                      onClick={() => navigate(-1)}
                    >
                      <ArrowLeft className="tw-w-4 tw-h-4" />
                    </div>
                    <div className="tw-size-16 tw-rounded-2xl tw-overflow-hidden tw-border tw-border-solid tw-border-slate-200 tw-shrink-0 tw-shadow-sm">
                      <img
                        src={profile}
                        className="tw-w-full tw-h-full tw-object-cover"
                        alt=""
                      />
                    </div>
                    <div className="tw-flex-1 tw-min-w-0">
                      <div className="tw-flex tw-items-center tw-gap-3.5 tw-flex-wrap tw-mb-1.5">
                        <h2 className="tw-text-xl tw-font-bold tw-text-slate-800 tw-m-0">
                          {leadDetail?.firstname} {leadDetail?.lastname}
                        </h2>
                        {leadDetail?.leadRating === "Hot" && (
                          <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-font-bold tw-text-rose-600 tw-bg-rose-50 tw-border tw-border-solid tw-border-rose-100 tw-px-2 tw-py-0.5 tw-rounded-md tw-uppercase">
                            Hot Lead{" "}
                            <img src={hot} className="tw-size-3.5" alt="" />
                          </span>
                        )}
                        {leadDetail?.leadRating === "Cold" && (
                          <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-font-bold tw-text-sky-600 tw-bg-sky-50 tw-border tw-border-solid tw-border-sky-100 tw-px-2 tw-py-0.5 tw-rounded-md tw-uppercase">
                            Cold Lead{" "}
                            <img src={cold} className="tw-size-3.5" alt="" />
                          </span>
                        )}
                        {leadDetail?.leadRating === "Warm" && (
                          <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-font-bold tw-text-amber-600 tw-bg-amber-50 tw-border tw-border-solid tw-border-amber-100 tw-px-2 tw-py-0.5 tw-rounded-md tw-uppercase">
                            Warm Lead{" "}
                            <img src={warm} className="tw-size-3.5" alt="" />
                          </span>
                        )}
                      </div>

                      {/* Action Chips and Timestamps */}
                      <div className="tw-flex tw-items-center tw-gap-4 tw-flex-wrap">
                        {/* Quick Communications */}
                        <div className="tw-flex tw-items-center tw-gap-2">
                          {leadDetail?.phoneNo && (
                            <NavLink
                              to={`tel:${leadDetail?.phoneNo}`}
                              className="tw-no-underline"
                            >
                              <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-slate-600 hover:tw-text-theme tw-bg-slate-50 hover:tw-bg-indigo-50/20 tw-border tw-border-solid tw-border-slate-200 hover:tw-border-theme/30 tw-rounded-lg tw-px-2.5 tw-py-1 tw-transition-all tw-cursor-pointer">
                                <Call className="tw-size-3.5 tw-shrink-0" />{" "}
                                Call
                              </span>
                            </NavLink>
                          )}

                          {(leadDetail?.phoneNo || leadDetail?.mobile) && (
                            <span
                              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-slate-600 hover:tw-text-[#25D366] tw-bg-slate-50 hover:tw-bg-emerald-50/20 tw-border tw-border-solid tw-border-slate-200 hover:tw-border-[#25D366]/30 tw-rounded-lg tw-px-2.5 tw-py-1 tw-transition-all tw-cursor-pointer"
                              onClick={() => {
                                const phone =
                                  leadDetail?.phoneNo ||
                                  leadDetail?.mobile ||
                                  "";
                                const name =
                                  `${leadDetail?.firstname || ""} ${leadDetail?.lastname || ""}`.trim();
                                navigate(
                                  `/whatsapp?leadId=${id}&phone=${encodeURIComponent(phone)}&name=${encodeURIComponent(name)}`,
                                );
                              }}
                            >
                              <MessageCircle className="tw-text-[#25D366] tw-w-4 tw-h-4" />
                              WhatsApp
                            </span>
                          )}

                          {!leadDetail?.isDummy &&
                            isEmailSetupView &&
                            !!leadDetail?.emailId && (
                              <EmailForm
                                payload={leadDetail}
                                recipientEmail={leadDetail?.emailId}
                                empId={leadDetail?.employeeId}
                                callback={() =>
                                  dispatch(
                                    getEmailHistoryByLeadId(leadDetail?.id),
                                  )
                                }
                              >
                                <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-slate-600 hover:tw-text-theme tw-bg-slate-50 hover:tw-bg-indigo-50/20 tw-border tw-border-solid tw-border-slate-200 hover:tw-border-theme/30 tw-rounded-lg tw-px-2.5 tw-py-1 tw-transition-all tw-cursor-pointer">
                                  <Email className="tw-size-3.5 tw-shrink-0" />{" "}
                                  Email
                                </span>
                              </EmailForm>
                            )}
                        </div>

                        {/* Divider */}
                        <div className="tw-hidden sm:tw-block tw-h-3 tw-w-[1px] tw-bg-slate-200" />

                        {/* Timestamps */}
                        <div className="tw-text-slate-400 tw-text-[11px] tw-font-medium tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                          {leadDetail?.createdDate && (
                            <span>
                              Created:{" "}
                              {moment(leadDetail?.createdDate).format(
                                "D MMM YYYY, h:mm A",
                              )}
                            </span>
                          )}
                          {leadDetail?.createdDate &&
                            leadDetail?.lastModifiedDate && (
                              <span className="tw-text-slate-300 tw-select-none">
                                •
                              </span>
                            )}
                          {leadDetail?.lastModifiedDate && (
                            <span>
                              Last Activity:{" "}
                              {moment(leadDetail?.lastModifiedDate).format(
                                "D MMM YYYY, h:mm A",
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button
                    className="!tw-bg-theme tw-font-semibold !tw-outline-0 !tw-border-theme !tw-text-white tw-text-sm tw-h-10 tw-rounded-xl tw-shadow-sm hover:tw-shadow tw-transition-all tw-flex tw-items-center tw-gap-1.5 tw-shrink-0"
                    onClick={() => {
                      setAddTask(true);
                    }}
                  >
                    <Plus className="tw-w-4 tw-h-4" />
                    <span>Add Tasks</span>
                  </Button>
                </div>

                <Stepsbar />

                <Tabs
                  activeKey={activeTab}
                  items={
                    isEmailSetupView && !leadDetail?.isDummy
                      ? items?.filter((d) => !!d?.isVisible)
                      : items
                          ?.filter((d) => !!d?.isVisible)
                          ?.filter((d) => d?.key !== "4")
                  }
                  onChange={(key) => {
                    setActiveTab(`${key}`);
                  }}
                  className="tw-border tw-border-solid !tw-border-slate-200/80 !tw-rounded-2xl tw-bg-white tw-shadow-sm tw-overflow-hidden"
                />
              </Col>
              <Col
                lg={6}
                className="tw-h-[calc(100vh-105px)] tw-flex tw-flex-col tw-gap-5 tw-overflow-y-auto custom-scrollbar tw-pr-1"
              >
                {/* Card 1: Lead Information */}
                <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 tw-rounded-2xl tw-shadow-sm tw-flex tw-flex-col tw-shrink-0">
                  <div className="tw-py-4 tw-px-5 tw-text-sm tw-font-bold tw-text-slate-800 tw-border-b tw-border-t-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-slate-100 tw-flex tw-items-center tw-gap-2.5 tw-shrink-0">
                    <img src={leadInfo} className="tw-size-4" alt="" />
                    <span>Lead Information</span>
                  </div>

                  <div className="tw-flex tw-flex-col tw-p-5 tw-gap-4">
                    {leadOwnerName && (
                      <div className="tw-bg-indigo-50/30 tw-rounded-xl tw-border tw-border-solid tw-border-indigo-100/50 tw-p-3.5 tw-flex tw-items-center tw-justify-between tw-transition-all hover:tw-shadow-sm">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-bg-theme tw-text-white tw-font-bold tw-size-9 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-xs tw-shadow-sm">
                            {leadOwnerName.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="tw-text-[10px] tw-text-slate-450 tw-font-bold tw-uppercase tw-tracking-wider tw-mb-0.5">
                              Lead Owner
                            </div>
                            <div className="tw-text-sm tw-font-bold tw-text-slate-700">
                              {leadOwnerName}
                            </div>
                          </div>
                        </div>
                        {canTransferLead && (
                          <Tooltip title="Transfer Ownership">
                            <button
                              type="button"
                              className="tw-flex tw-border-none tw-cursor-pointer tw-items-center tw-justify-center tw-bg-white hover:tw-bg-theme hover:tw-text-white tw-text-slate-500 tw-size-7 tw-rounded-full tw-border tw-border-solid tw-border-slate-200 tw-transition-all tw-shadow-sm hover:tw-shadow"
                              onClick={() => setIsTransfer(true)}
                            >
                              <ArrowLeftRight className="tw-w-3 tw-h-3" />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    )}

                    <div className="tw-flex tw-flex-col tw-gap-1 tw-text-slate-600 tw-pt-1">
                      {leadDetail?.title && (
                        <div className="tw-flex tw-items-center tw-gap-3 tw-py-1.5 tw-px-2 tw-rounded-xl hover:tw-bg-slate-50 tw-transition-colors">
                          <Designation className="tw-size-4 tw-text-slate-400 tw-shrink-0" />
                          <span
                            className="tw-text-xs tw-font-semibold tw-text-slate-700 tw-truncate"
                            title={leadDetail?.title}
                          >
                            {leadDetail?.title}
                          </span>
                        </div>
                      )}

                      {leadDetail?.emailId && (
                        <div className="tw-flex tw-items-center tw-gap-3 tw-py-1.5 tw-px-2 tw-rounded-xl hover:tw-bg-slate-50 tw-transition-colors">
                          <MailOpen className="tw-size-4 tw-text-slate-400 tw-shrink-0" />
                          <span
                            className="tw-text-xs tw-font-semibold tw-text-slate-700 tw-truncate tw-min-w-0 tw-flex-1"
                            title={leadDetail?.emailId}
                          >
                            {leadDetail?.emailId}
                          </span>
                        </div>
                      )}

                      {(leadDetail?.phoneNo || leadDetail?.mobile) && (
                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-py-1.5 tw-px-2 tw-rounded-xl hover:tw-bg-slate-50 tw-transition-colors">
                          <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0 tw-flex-1">
                            <Call className="tw-size-4 tw-text-slate-400 tw-shrink-0" />
                            <span
                              className="tw-text-xs tw-font-semibold tw-text-slate-700 tw-truncate"
                              title={leadDetail?.phoneNo || leadDetail?.mobile}
                            >
                              {leadDetail?.phoneNo || leadDetail?.mobile}
                            </span>
                          </div>
                          <Tooltip title="Chat on WhatsApp">
                            <MessageCircle
                              className="tw-text-[#25D366] tw-w-4 tw-h-4 tw-cursor-pointer hover:tw-scale-110 tw-transition-transform tw-shrink-0"
                              onClick={() => {
                                const phone =
                                  leadDetail?.phoneNo ||
                                  leadDetail?.mobile ||
                                  "";
                                const name =
                                  `${leadDetail?.firstname || ""} ${leadDetail?.lastname || ""}`.trim();
                                navigate(
                                  `/whatsapp?leadId=${id}&phone=${encodeURIComponent(phone)}&name=${encodeURIComponent(name)}`,
                                );
                              }}
                            />
                          </Tooltip>
                        </div>
                      )}

                      {leadDetail?.website && (
                        <div className="tw-flex tw-items-center tw-gap-3 tw-py-1.5 tw-px-2 tw-rounded-xl hover:tw-bg-slate-50 tw-transition-colors">
                          <WebLink className="tw-size-4 tw-text-slate-400 tw-shrink-0" />
                          <span
                            className="tw-text-xs tw-font-semibold tw-text-slate-700 tw-truncate tw-underline hover:tw-text-theme tw-transition-colors tw-min-w-0 tw-flex-1"
                            title={leadDetail?.website}
                          >
                            <a
                              href={
                                leadDetail?.website?.startsWith("http")
                                  ? leadDetail.website
                                  : `https://${leadDetail?.website}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="tw-text-inherit"
                            >
                              {leadDetail?.website}
                            </a>
                          </span>
                        </div>
                      )}

                      {leadDetail?.address && (
                        <div className="tw-flex tw-items-start tw-gap-3 tw-py-1.5 tw-px-2 tw-rounded-xl hover:tw-bg-slate-50 tw-transition-colors">
                          <Location className="tw-size-4 tw-text-slate-400 tw-shrink-0 tw-mt-0.5" />
                          <span
                            className="tw-text-xs tw-font-semibold tw-text-slate-700 tw-leading-relaxed tw-break-words tw-whitespace-normal tw-min-w-0 tw-flex-1"
                            title={leadDetail?.address}
                          >
                            {leadDetail?.address}
                          </span>
                        </div>
                      )}



                      {!!leadDetail?.tags?.length && (
                        <div className="tw-flex tw-items-start tw-gap-3 tw-py-1.5 tw-px-2 tw-rounded-xl hover:tw-bg-slate-50 tw-transition-colors">
                          <TagIcon className="tw-text-slate-400 tw-w-4 tw-h-4 tw-mt-0.5 tw-shrink-0" />
                          <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                            {leadDetail?.tags?.map((tag, i) => (
                              <Tag
                                key={i}
                                className="tw-rounded-full tw-bg-slate-50 tw-border tw-border-solid tw-border-slate-200/80 tw-text-slate-550 tw-text-[11px] tw-font-semibold tw-py-0.5 tw-px-2 tw-m-0 tw-cursor-pointer hover:tw-text-theme hover:tw-border-theme/35 tw-transition-colors"
                                onClick={() =>
                                  navigate(`/search-lead?Notes Tags=${tag}`)
                                }
                              >
                                {tag}
                              </Tag>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Card 2: Company Detail */}
                <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 tw-rounded-2xl tw-shadow-sm tw-flex tw-flex-col tw-shrink-0">
                  <div className="tw-py-4 tw-px-5 tw-text-sm tw-font-bold tw-text-slate-800 tw-border-b tw-border-t-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-slate-100 tw-flex tw-items-center tw-gap-2.5 tw-shrink-0">
                    <Zoom className="tw-size-4" />
                    <span>Company Detail</span>
                  </div>

                  {leadDetail?.industryText || leadDetail?.companyName ? (
                    <div className="tw-flex tw-flex-col tw-p-5 tw-gap-1">
                      {leadDetail?.companyName && (
                        <div className="tw-flex tw-items-center tw-gap-3 tw-py-1.5 tw-px-2 tw-rounded-xl hover:tw-bg-slate-50 tw-transition-colors">
                          <span className="tw-text-[10px] tw-text-slate-400 tw-font-bold tw-uppercase tw-tracking-wider tw-w-28 tw-shrink-0">
                            Company Name
                          </span>
                          <span className="tw-text-xs tw-font-semibold tw-text-slate-700 tw-truncate">
                            {leadDetail?.companyName}
                          </span>
                        </div>
                      )}
                      {leadDetail?.industryText && (
                        <div className="tw-flex tw-items-center tw-gap-3 tw-py-1.5 tw-px-2 tw-rounded-xl hover:tw-bg-slate-50 tw-transition-colors">
                          <span className="tw-text-[10px] tw-text-slate-400 tw-font-bold tw-uppercase tw-tracking-wider tw-w-28 tw-shrink-0">
                            Industry
                          </span>
                          <span className="tw-text-xs tw-font-semibold tw-text-slate-700 tw-truncate">
                            {leadDetail?.industryText}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <AttractiveEmptyState
                      icon={Building2}
                      title="No company details found"
                      description="Company details will appear once loaded."
                    />
                  )}
                </div>
              </Col>
            </Row>
          </div>
        </div>

        <a className="scroll-to-top rounded" href="#page-top">
          <ChevronUp className="tw-w-4 tw-h-4" />{" "}
        </a>
      </div>

      <Modal
        title={null}
        open={isAddTask}
        destroyOnClose
        footer={null}
        onCancel={() => {
          setAddTask(false);
          setEditTask(null);
          setTask();
        }}
        centered
        className="delete-popup"
        width={550}
      >
        <div className="tw-bg-slate-50 tw-text-slate-800 tw-text-lg tw-font-bold tw-py-4 tw-px-6 tw-rounded-t-2xl tw-border tw-border-solid tw-border-slate-100/50">
          {editTask?.id ? "Edit Task" : "Add Task"}
        </div>
        <TaskForm
          task={task}
          setTask={setTask}
          initialValues={editTask}
          setActiveTab={setActiveTab}
          handleClose={() => {
            setAddTask(false);
            setEditTask(null);
            setTask();
          }}
        />
      </Modal>

      <LeadActivityDrawer
        lead={historyFieldFilter ? leadDetail : null}
        initialFieldFilter={historyFieldFilter}
        onClose={() => setHistoryFieldFilter(undefined)}
      />

      <TransferLeadsDrawer
        open={isTransfer}
        onClose={() => setIsTransfer(false)}
        onSubmit={async (data) => {
          const res = await dispatch(
            transferLeads({
              ...data,
              leadIds: [leadDetail?.id],
            }),
          );

          if (res?.status === 200) {
            showAlert(
              "success",
              "Lead transferred successfully",
              <span>
                Lead transferred to <strong>{data?.newOwnerName}</strong>
              </span>,
            );
            dispatch(getLeadDetails(id));
          } else {
            showAlert(
              "error",
              res?.response?.data?.title || "Unable to transfer lead.",
            );
          }
        }}
        selectedCount={1}
        currentOwner={leadOwnerName}
        isAdmin={currentUser?.role === userRoles?.admin}
        selectedRecords={[{ ...leadDetail, executiveData: leadDetail }]}
        user={currentUser}
      />
    </>
  );
};

export default LeadDetails;
